// src/pages/portal/Resumen.js
import React, { useEffect, useState, useMemo } from 'react';
import { supabase } from '../../supabaseClient';
import {
  Loader2, DollarSign, User, Calendar, TrendingUp, AlertCircle, TrendingDown, History
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from 'recharts';
import { useAuth } from '../../AuthContext';

// --- Componentes Reutilizables (Sin Cambios) ---
const StatCard = ({ title, value, icon, bgColor, iconColor }) => (
  <div className={`p-6 rounded-xl shadow-md flex-grow ${bgColor}`}>
    <div className="flex justify-between items-start">
      <p className="font-semibold text-gray-700">{title}</p>
      <div className={`p-2 rounded-full ${iconColor}`}>{icon}</div>
    </div>
    <p className="text-3xl font-bold text-gray-800 mt-2">{value}</p>
  </div>
);

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#AF19FF', '#FF1943'];

// --- Componente Principal ---
const Resumen = () => {
  const { userData, userType, loading: authLoading } = useAuth();
  const [students, setStudents] = useState([]); // Can hold student or teacher data
  const [selectedStudentId, setSelectedStudentId] = useState(''); // Can be student or teacher ID
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Default dates (Sin Cambios)
  const today = new Date().toISOString().split('T')[0];
  const thirtyDaysAgo = new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0];
  const [startDate, setStartDate] = useState(thirtyDaysAgo);
  const [endDate, setEndDate] = useState(today);

  // --- Load user data (Sin Cambios) ---
  useEffect(() => {
    if (!userData) return;

    if (userType === 'parent' && userData?.students) {
      setStudents(userData.students);
      if (userData.students.length > 0) setSelectedStudentId(userData.students[0].id);
    } else if (userType === 'student') {
      setStudents([userData]);
      setSelectedStudentId(userData.id);
    } else if (userType === 'teacher') {
      setStudents([userData]);
      setSelectedStudentId(userData.id);
    }
  }, [userData, userType]);

  // Find the selected user (student or teacher) based on ID (Sin Cambios)
  const selectedUser = useMemo(
    () => students.find(s => s.id === selectedStudentId),
    [students, selectedStudentId]
  );

  // --- Load dashboard data (Sin Cambios) ---
  useEffect(() => {
    if (!selectedStudentId) {
      setLoading(false); 
      return;
    }

    const fetchDashboardData = async () => {
      setLoading(true);
      try {
        const isTeacher = userType === 'teacher';
        const rpcName = isTeacher ? 'get_teacher_dashboard_data' : 'get_student_dashboard_data';
        const idParamName = isTeacher ? 'teacher_id_param' : 'student_id_param';

        const { data, error } = await supabase.rpc(rpcName, {
          [idParamName]: selectedStudentId,
          start_date_param: startDate,
          end_date_param: endDate
        });

        if (error) throw error;

        if (!data || data.length === 0) {
           setDashboardData(null); 
        } else {
          const raw = data[0];
          setDashboardData({
            total_spending_period: parseFloat(raw.total_spending_period) || 0,
            total_recharged_period: parseFloat(raw.total_recharged_period) || 0,
            daily_activity: raw.daily_activity || [],
            spending_by_category: raw.spending_by_category || [],
            total_spending_lifetime: parseFloat(raw.total_spending_lifetime) || 0,
            total_recharged_lifetime: parseFloat(raw.total_recharged_lifetime) || 0,
          });
        }
      } catch (err) {
        console.error("❌ Error fetching dashboard data:", err); 
        setDashboardData(null); 
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [selectedStudentId, startDate, endDate, userType]);


  // --- Calculate Period Net Balance (Recargas - Gastos) (Sin Cambios) ---
  const balanceDelPeriodo = useMemo(() => {
    if (!dashboardData) return 0;
    const totalRecharged = dashboardData.total_recharged_period || 0;
    const totalSpent = dashboardData.total_spending_period || 0;
    return totalRecharged - totalSpent;
  }, [dashboardData]);

  
  // --- *** INICIO DE CORRECCIÓN *** ---

  // El Saldo Total (Histórico) AHORA se calcula usando los datos del dashboard
  const saldoTotalCalculado = useMemo(() => {
      if (!dashboardData) return 0; // Depende de dashboardData
      const recargas = dashboardData.total_recharged_lifetime || 0;
      const gastos = dashboardData.total_spending_lifetime || 0;
      // Cálculo: 15.00 - 13.50 = 1.50
      return recargas - gastos;
  }, [dashboardData]); // Depende de dashboardData

  // La alerta de saldo bajo AHORA usa el saldo calculado
  const saldoParaAlerta = saldoTotalCalculado;

  // --- *** FIN DE CORRECCIÓN *** ---


  // --- Render Logic ---
  if (authLoading) {
    return (
      <div className="flex justify-center items-center h-full">
        <Loader2 className="animate-spin text-orange-500" size={48} />
      </div>
    );
  }

  if (!loading && (!userType || (userType !== 'parent' && userType !== 'student' && userType !== 'teacher'))) {
    // ... (Mensaje de Error de Configuración sin cambios)
    return (
      <div className="flex flex-col items-center justify-center h-full p-8 text-center bg-white rounded-lg shadow-md">
        <AlertCircle className="w-16 h-16 text-red-500 mb-4" />
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Error de Configuración</h2>
        <p className="text-gray-600">
          Su tipo de usuario ({userType || 'desconocido'}) no tiene un dashboard asignado.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* --- Header and User Selector (Sin Cambios) --- */}
      <div className="flex flex-wrap justify-between items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Dashboard de Actividad</h1>
          <p className="text-gray-500">Un resumen visual de consumos y recargas.</p>
        </div>
        {userType === 'parent' && students.length > 1 && (
          <select
            value={selectedStudentId}
            onChange={e => setSelectedStudentId(e.target.value)}
            className="p-2 border rounded-md bg-white shadow-sm"
          >
            {students.map(s => (
              <option key={s.id} value={s.id}>
                {s.first_name} {s.first_last_name}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* --- Date Range Selector (Sin Cambios) --- */}
      <div className="p-4 bg-white rounded-xl shadow-md flex flex-wrap items-center gap-4 text-sm">
        <div className="flex items-center gap-2">
          <Calendar size={18} />
          <span>Rango de Fechas:</span>
        </div>
        <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="border rounded-md p-2" />
        <span>-</span>
        <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="border rounded-md p-2" />
      </div>

      {/* --- Loading / No Data / Data Loaded --- */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <Loader2 className="animate-spin text-orange-500" size={48} />
        </div>
      ) : !selectedUser || !dashboardData ? (
        <p className="text-center text-gray-600 p-8 bg-white rounded-xl shadow-md">
          No se encontró información para el {userType === 'teacher' ? 'maestro' : 'alumno'} seleccionado en este período.
        </p>
      ) : (
        <>
          {/* --- Alerta de Saldo (AHORA USA EL CÁLCULO) --- */}
          { (saldoParaAlerta < 0 || (saldoParaAlerta > 0 && saldoParaAlerta < 5)) && (
            <div className={`flex items-center gap-4 p-4 rounded-lg shadow-sm border-l-4 ${
              saldoParaAlerta < 0 ? 'bg-red-50 border-red-500' : 'bg-yellow-100 border-yellow-500'
            }`}>
              <AlertCircle className={saldoParaAlerta < 0 ? 'text-red-600' : 'text-yellow-600'} />
              <div>
                <p className={`font-bold ${saldoParaAlerta < 0 ? 'text-red-800' : 'text-yellow-800'}`}>
                  {saldoParaAlerta < 0 ? 'Saldo Negativo (Total)' : 'Saldo bajo (Total)'}
                </p>
                <p className={`text-sm ${saldoParaAlerta < 0 ? 'text-red-700' : 'text-yellow-700'}`}>
                  El saldo total es inferior a $5.00, considere realizar una recarga para continuar comprando.
                </p>
              </div>
            </div>
          )}
          {/* --- Fin Alerta --- */}


          {/* Dashboard Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Column (Stats and Charts) */}
            <div className="lg:col-span-2 space-y-8">
              
              {/* --- 6 Tarjetas (TARJETA 1 CORREGIDA) --- */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                
                {/* TARJETA 1: Saldo Acumulado (Total) - AHORA USA EL CÁLCULO */}
                <StatCard
                  title="Saldo Actual (Calculado)"
                  value={`$${saldoTotalCalculado.toFixed(2)}`}
                  icon={<DollarSign size={24} />}
                  bgColor={saldoTotalCalculado >= 0 ? "bg-green-50" : "bg-red-50"}
                  iconColor={saldoTotalCalculado >= 0 ? "bg-green-200 text-green-700" : "bg-red-200 text-red-700"}
                />

                {/* TARJETA 2: Gasto (Histórico) */}
                <StatCard
                  title="Gasto Histórico"
                  value={`$${dashboardData.total_spending_lifetime.toFixed(2)}`}
                  icon={<History size={24} />}
                  bgColor="bg-orange-50"
                  iconColor="bg-orange-200 text-orange-700"
                />

                {/* TARJETA 3: Recarga (Histórica) */}
                <StatCard
                  title="Recarga Histórica"
                  value={`$${dashboardData.total_recharged_lifetime.toFixed(2)}`}
                  icon={<History size={24} />}
                  bgColor="bg-teal-50"
                  iconColor="bg-teal-200 text-teal-700"
                />

                {/* TARJETA 4: Balance Filtrado (Período) */}
                <StatCard
                  title="Balance Neto (Período)"
                  value={`$${balanceDelPeriodo.toFixed(2)}`}
                  icon={<TrendingUp size={24} />}
                  bgColor={balanceDelPeriodo >= 0 ? "bg-blue-50" : "bg-rose-50"} 
                  iconColor={balanceDelPeriodo >= 0 ? "bg-blue-200 text-blue-700" : "bg-rose-200 text-rose-700"}
                />
                
                {/* TARJETA 5: Total Gastado (Período) */}
                <StatCard
                  title="Total Gastado (Período)"
                  value={`$${dashboardData.total_spending_period.toFixed(2)}`}
                  icon={<TrendingDown size={24} />} 
                  bgColor="bg-gray-100" 
                  iconColor="bg-gray-300 text-gray-700"
                />

                {/* TARJETA 6: Total Recargado (Período) */}
                <StatCard
                  title="Total Recargado (Período)"
                  value={`$${dashboardData.total_recharged_period.toFixed(2)}`}
                  icon={<TrendingUp size={24} />}
                  bgColor="bg-gray-100" 
                  iconColor="bg-gray-300 text-gray-700"
                />
              </div>
              {/* --- Fin Tarjetas --- */}


              {/* Daily Activity Chart (Sin Cambios) */}
              <div className="bg-white p-6 rounded-xl shadow-md">
                <h3 className="text-xl font-bold mb-4">Actividad Diaria</h3>
                {dashboardData.daily_activity && dashboardData.daily_activity.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={dashboardData.daily_activity}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="date"
                        tickFormatter={(tick) => new Date(tick + 'T00:00:00').toLocaleDateString('es-SV', { day: 'numeric', month: 'short' })}
                      />
                      <YAxis />
                      <Tooltip formatter={(value) => [`$${value.toFixed(2)}`, 'Gasto']} />
                      <Bar dataKey="gasto" fill="#82ca9d" name="Gasto"/>
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-center text-gray-500 py-4">No hay actividad registrada en este período.</p>
                )}
              </div>
            </div>

            {/* Sidebar Column (User Info and Category Chart) */}
            <div className="space-y-8">
              {/* User Info Card (Sin Cambios) */}
              <div className="p-6 bg-white rounded-xl shadow-md">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-full bg-indigo-100"><User className="text-indigo-800" /></div>
                  <div>
                    <p className="font-bold text-lg">{selectedUser.first_name} {selectedUser.first_last_name}</p>
                    {userType === 'teacher' ? (
                      <p className="text-sm text-gray-500">Maestro(a) - {selectedUser.schools?.name || 'Escuela no asignada'}</p>
                    ) : (
                      <p className="text-sm text-gray-500">{selectedUser.grade} "{selectedUser.section}"</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Spending by Category Chart (Sin Cambios) */}
              <div className="bg-white p-6 rounded-xl shadow-md">
                <h3 className="text-xl font-bold mb-4">Gastos por Categoría</h3>
                 {dashboardData.spending_by_category && dashboardData.spending_by_category.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={dashboardData.spending_by_category}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        fill="#8884d8"
                        labelLine={false}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      >
                        {dashboardData.spending_by_category.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => `$${value.toFixed(2)}`} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                 ) : (
                    <p className="text-center text-gray-500 py-4">No hay gastos registrados en este período.</p>
                 )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Resumen;